// frontend/src/pages/AdminPage.js
import React, { useEffect, useState } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import api from '../api'
import Navbar from '../components/Navbar'
import { useCurrentBasho } from '../hooks/useCurrentBasho'

const font = "'Noto Serif JP', Georgia, serif"
const ink = '#1A0A00'
const muted = '#8A7A5A'
const accent = '#C41E3A'

export default function AdminPage() {
  const [users, setUsers] = useState([])
  const [loading, setLoading] = useState(true)
  const [actionLoading, setActionLoading] = useState({})
  const navigate = useNavigate()
  const currentUser = localStorage.getItem('username')
  const { basho: currentBasho, day: currentDay, status: bashoStatus } = useCurrentBasho()
  const bashoActive = bashoStatus === 'active'

  // ── User action confirmation state ────────────────────────────────────────
  const [confirmAction, setConfirmAction] = useState(null) // { type: 'revoke'|'delete', username }

  // ── Clear data modal state ────────────────────────────────────────────────
  const [clearStep, setClearStep] = useState(0) // 0=closed, 1=confirm, 2=type DELETE
  const [clearInput, setClearInput] = useState('')
  const [clearLoading, setClearLoading] = useState(false)
  const [clearError, setClearError] = useState('')

  // ── Picks reminder state ──────────────────────────────────────────────────
  const [reminderLoading, setReminderLoading] = useState(false)
  const [reminderMsg, setReminderMsg] = useState('')

  // ── Announce modal state ──────────────────────────────────────────────────
  const [announceOpen, setAnnounceOpen] = useState(false)
  const [announceSubject, setAnnounceSubject] = useState('')
  const [announceBody, setAnnounceBody] = useState('')
  const [announceLoading, setAnnounceLoading] = useState(false)
  const [announceMsg, setAnnounceMsg] = useState('')
  const [announceConfirm, setAnnounceConfirm] = useState(false)

  // ── Groups state ─────────────────────────────────────────────────────────────
  const [groups, setGroups] = useState([])
  const [newGroupName, setNewGroupName] = useState('')
  const [groupCreateMsg, setGroupCreateMsg] = useState('')
  const [groupCreateLoading, setGroupCreateLoading] = useState(false)
  const [groupDeleteLoading, setGroupDeleteLoading] = useState({}) // { id: true }
  const [groupAssignLoading, setGroupAssignLoading] = useState({}) // { username: true }
  const [groupEditId, setGroupEditId] = useState(null)             // id of group being renamed
  const [groupEditName, setGroupEditName] = useState('')
  const [groupRenameLoading, setGroupRenameLoading] = useState(false)
  const [groupRenameMsg, setGroupRenameMsg] = useState('')

  // ── Unlock picks state ───────────────────────────────────────────────────────
  const [unlockOpen, setUnlockOpen] = useState(false)
  const [unlockBasho, setUnlockBasho] = useState(currentBasho || '')
  const [unlockDay, setUnlockDay] = useState(currentDay || '')
  const [unlockLoading, setUnlockLoading] = useState(false)
  const [unlockMsg, setUnlockMsg] = useState('')
  const [activeUnlocks, setActiveUnlocks] = useState([])
  const [relockLoading, setRelockLoading] = useState({}) // { "basho/day": true }

  // ── SmartBot state ────────────────────────────────────────────────────────
  const [botModel, setBotModel] = useState(null)
  const [botModelLoading, setBotModelLoading] = useState(false)
  const [botRetrainLoading, setBotRetrainLoading] = useState(false)
  const [botRetrainMsg, setBotRetrainMsg] = useState('')
  const [botPicks, setBotPicks] = useState([])
  const [botPicksLoading, setBotPicksLoading] = useState(false)
  const [botPicksOpen, setBotPicksOpen] = useState(false)
  const [smartBotPickLoading, setSmartBotPickLoading] = useState(false)
  const [smartBotPickMsg, setSmartBotPickMsg] = useState('')

  const load = () => {
    setLoading(true)
    api.get('/admin/users')
      .then(res => setUsers(res.data.users || []))
      .catch(err => { if (err.response?.status === 403) navigate(`/picks/${currentBasho}/${currentDay}`) })
      .finally(() => setLoading(false))
  }

  const loadUnlocks = () => {
    api.get('/admin/pick-unlocks')
      .then(res => setActiveUnlocks(res.data.unlocks || []))
      .catch(() => {})
  }

  const loadGroups = () => {
    api.get('/admin/groups')
      .then(res => setGroups(res.data.groups || []))
      .catch(() => {})
  }

  const loadBotModel = () => {
    setBotModelLoading(true)
    api.get('/admin/smart-bot/model')
      .then(res => setBotModel(res.data))
      .catch(() => {})
      .finally(() => setBotModelLoading(false))
  }

  const loadBotPicks = () => {
    setBotPicksLoading(true)
    api.get('/admin/smart-bot/picks')
      .then(res => setBotPicks(res.data.picks || []))
      .catch(() => {})
      .finally(() => setBotPicksLoading(false))
  }

  const retrain = async () => {
    setBotRetrainLoading(true); setBotRetrainMsg('')
    try {
      const res = await api.post('/admin/smart-bot/retrain')
      setBotRetrainMsg(`Model v${res.data.version} trained — accuracy ${Math.round(res.data.accuracy * 100)}% on ${res.data.trainedOn} bouts`)
      loadBotModel()
      setTimeout(() => setBotRetrainMsg(''), 8000)
    } catch (err) {
      setBotRetrainMsg(err.response?.data?.error || 'Retrain failed')
      setTimeout(() => setBotRetrainMsg(''), 6000)
    } finally { setBotRetrainLoading(false) }
  }

  const triggerSmartBotPicks = async () => {
    setSmartBotPickLoading(true); setSmartBotPickMsg('')
    try {
      const res = await api.post('/admin/smart-bot/pick')
      setSmartBotPickMsg(res.data.skipped === 'no_model'
        ? 'No trained model — run backfill then retrain first'
        : `Robotofuji submitted ${res.data.count} picks for ${res.data.basho} day ${res.data.day}`)
      setTimeout(() => setSmartBotPickMsg(''), 6000)
    } catch (err) {
      setSmartBotPickMsg(err.response?.data?.error || 'Failed')
      setTimeout(() => setSmartBotPickMsg(''), 6000)
    } finally { setSmartBotPickLoading(false) }
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); loadUnlocks(); loadGroups(); loadBotModel() }, [])

  const act = async (username, action) => {
    setActionLoading(prev => ({ ...prev, [username]: action }))
    try {
      await api.post(`/admin/users/${username}/${action}`)
      load()
    } catch (err) {
      alert(err.response?.data?.error || 'Action failed')
    } finally {
      setActionLoading(prev => ({ ...prev, [username]: null }))
    }
  }

  const deleteUser = async (username) => {
    setActionLoading(prev => ({ ...prev, [username]: 'delete' }))
    try {
      await api.delete(`/admin/users/${username}`)
      load()
    } catch (err) {
      alert(err.response?.data?.error || 'Delete failed')
    } finally {
      setActionLoading(prev => ({ ...prev, [username]: null }))
    }
  }

  // ── Group handlers ────────────────────────────────────────────────────────
  const createGroup = async () => {
    if (!newGroupName.trim()) return
    setGroupCreateLoading(true); setGroupCreateMsg('')
    try {
      await api.post('/admin/groups', { name: newGroupName.trim() })
      setNewGroupName('')
      setGroupCreateMsg('Group created.')
      loadGroups()
      setTimeout(() => setGroupCreateMsg(''), 3000)
    } catch (err) {
      setGroupCreateMsg(err.response?.data?.error || 'Create failed')
      setTimeout(() => setGroupCreateMsg(''), 4000)
    } finally { setGroupCreateLoading(false) }
  }

  const deleteGroup = async (id) => {
    setGroupDeleteLoading(prev => ({ ...prev, [id]: true }))
    try {
      await api.delete(`/admin/groups/${id}`)
      loadGroups(); load() // refresh users too (their group may have changed)
    } catch (err) {
      alert(err.response?.data?.error || 'Delete failed')
    } finally { setGroupDeleteLoading(prev => ({ ...prev, [id]: false })) }
  }

  const renameGroup = async (id) => {
    if (!groupEditName.trim()) return
    setGroupRenameLoading(true); setGroupRenameMsg('')
    try {
      await api.put(`/admin/groups/${id}`, { name: groupEditName.trim() })
      setGroupEditId(null)
      setGroupEditName('')
      loadGroups()
    } catch (err) {
      setGroupRenameMsg(err.response?.data?.error || 'Rename failed')
      setTimeout(() => setGroupRenameMsg(''), 4000)
    } finally { setGroupRenameLoading(false) }
  }

  const assignGroup = async (username, groupId) => {
    setGroupAssignLoading(prev => ({ ...prev, [username]: true }))
    try {
      await api.put(`/admin/users/${username}/group`, { groupId: parseInt(groupId, 10) })
      load()
    } catch (err) {
      alert(err.response?.data?.error || 'Assignment failed')
    } finally { setGroupAssignLoading(prev => ({ ...prev, [username]: false })) }
  }

  // ── Clear data handlers ───────────────────────────────────────────────────
  const openClear = () => { setClearStep(1); setClearInput(''); setClearError('') }
  const closeClear = () => { setClearStep(0); setClearInput(''); setClearError('') }

  const doClear = async () => {
    if (clearInput !== 'DELETE') return
    setClearLoading(true)
    setClearError('')
    try {
      const res = await api.post('/admin/clear-game-data')
      closeClear()
      alert(`Cleared: ${res.data.deletedPicks} picks, ${res.data.deletedResults} results deleted.`)
    } catch (err) {
      setClearError(err.response?.data?.error || 'Clear failed')
    } finally {
      setClearLoading(false)
    }
  }

  // ── Reminder handler ──────────────────────────────────────────────────────
  const sendReminder = async () => {
    setReminderLoading(true)
    setReminderMsg('')
    try {
      const res = await api.post('/admin/send-picks-reminder')
      setReminderMsg(`Sent to ${res.data.sent} of ${res.data.total} users`)
      setTimeout(() => setReminderMsg(''), 5000)
    } catch (err) {
      setReminderMsg(err.response?.data?.error || 'Send failed')
      setTimeout(() => setReminderMsg(''), 5000)
    } finally {
      setReminderLoading(false)
    }
  }

  // ── Announce handlers ─────────────────────────────────────────────────────
  // ── Unlock picks handlers ─────────────────────────────────────────────────────
  const openUnlock = () => {
    setUnlockOpen(true)
    setUnlockBasho(currentBasho || '')
    setUnlockDay(currentDay ? String(currentDay) : '')
    setUnlockMsg('')
  }
  const closeUnlock = () => { setUnlockOpen(false); setUnlockMsg('') }
  const doUnlock = async () => {
    if (!unlockBasho || !unlockDay) return
    setUnlockLoading(true)
    setUnlockMsg('')
    try {
      await api.post(`/admin/unlock-picks/${unlockBasho}/${unlockDay}`)
      setUnlockMsg('Picks unlocked — results preserved.')
      loadUnlocks()
      setTimeout(() => { closeUnlock() }, 1500)
    } catch (err) {
      setUnlockMsg(err.response?.data?.error || 'Unlock failed')
    } finally {
      setUnlockLoading(false)
    }
  }
  const doRelock = async (basho, day) => {
    const key = `${basho}/${day}`
    setRelockLoading(prev => ({ ...prev, [key]: true }))
    try {
      await api.delete(`/admin/unlock-picks/${basho}/${day}`)
      loadUnlocks()
    } catch (err) {
      alert(err.response?.data?.error || 'Re-lock failed')
    } finally {
      setRelockLoading(prev => ({ ...prev, [key]: false }))
    }
  }

  const openAnnounce = () => {
    setAnnounceOpen(true)
    setAnnounceSubject('')
    setAnnounceBody('')
    setAnnounceMsg('')
    setAnnounceConfirm(false)
  }
  const closeAnnounce = () => { setAnnounceOpen(false); setAnnounceConfirm(false) }
  const approvedCount = users.filter(u => u.status === 'approved' && u.email).length

  const sendAnnouncement = async () => {
    setAnnounceLoading(true)
    setAnnounceMsg('')
    try {
      const res = await api.post('/admin/announce', { subject: announceSubject, body: announceBody })
      setAnnounceMsg(`Sent to ${res.data.sent} users`)
      setAnnounceConfirm(false)
      setTimeout(() => { closeAnnounce() }, 3000)
    } catch (err) {
      setAnnounceMsg(err.response?.data?.error || 'Send failed')
      setAnnounceConfirm(false)
    } finally {
      setAnnounceLoading(false)
    }
  }

  const pending = users.filter(u => u.status === 'pending')
  const all = [...users].sort((a, b) => {
    const order = { pending: 0, approved: 1, rejected: 2 }
    return order[a.status] - order[b.status]
  })

  const statusColor = {
    pending: '#8A6A1A',
    approved: '#1A6A1A',
    rejected: accent,
  }

  return (
    <div style={s.page}>
      <Navbar currentPage="admin" />
      <div style={s.container}>
        <div style={s.header}>
          <div>
            <div style={s.redLine} />
            <h1 style={s.title}>Admin Panel</h1>
          </div>
          <div style={s.headerRight}>
            <Link to={`/picks/${currentBasho}/${currentDay}`} style={s.backLink}>← Back to Picks</Link>
          </div>
        </div>

        {pending.length > 0 && (
          <div style={s.pendingBanner}>
            <span style={s.pendingBannerText}>
              {pending.length} account{pending.length > 1 ? 's' : ''} awaiting approval
            </span>
          </div>
        )}

        {/* ── Email tools ── */}
        <section style={s.section}>
          <h2 style={s.sectionTitle}>Email Tools</h2>
          <div style={s.toolRow}>

            {/* Send picks reminder */}
            <div style={s.toolCard}>
              <div style={s.toolCardTitle}>Picks Reminder</div>
              <p style={s.toolCardDesc}>
                Send today's picks reminder now — only goes to users with reminders enabled who haven't picked yet.
              </p>
              <div style={s.toolCardFooter}>
                <button
                  onClick={sendReminder}
                  disabled={reminderLoading || !bashoActive}
                  title={!bashoActive ? 'No active basho' : undefined}
                  style={s.toolBtn(!bashoActive)}
                >
                  {reminderLoading ? 'Sending…' : 'Send reminder now'}
                </button>
                {reminderMsg && <span style={s.toolMsg(reminderMsg.includes('failed') || reminderMsg.includes('Error'))}>{reminderMsg}</span>}
              </div>
            </div>

            {/* Announce */}
            <div style={s.toolCard}>
              <div style={s.toolCardTitle}>Announce to All Users</div>
              <p style={s.toolCardDesc}>
                Send a custom email to all approved users, regardless of their reminder preference.
              </p>
              <div style={s.toolCardFooter}>
                <button onClick={openAnnounce} style={s.toolBtn(false)}>
                  Compose announcement
                </button>
              </div>
            </div>

            {/* Clear game data */}
            <div style={{ ...s.toolCard, borderColor: `${accent}44` }}>
              <div style={{ ...s.toolCardTitle, color: accent }}>Clear Game Data</div>
              <p style={s.toolCardDesc}>
                Delete all picks and results. Cannot run once picks have been submitted for the active basho.
              </p>
              <div style={s.toolCardFooter}>
                <button onClick={openClear} style={{ ...s.toolBtn(false), color: accent, borderColor: `${accent}66` }}>
                  Clear game data…
                </button>
              </div>
            </div>

            {/* Unlock picks */}
            <div style={s.toolCard}>
              <div style={s.toolCardTitle}>Unlock Picks</div>
              <p style={s.toolCardDesc}>
                Delete results for a day to re-enable pick editing. Users can then change their picks.
              </p>
              <div style={s.toolCardFooter}>
                <button onClick={openUnlock} style={s.toolBtn(false)}>
                  Unlock picks…
                </button>
              </div>
            </div>

          </div>
        </section>

        {/* ── Active unlocks ── */}
        {activeUnlocks.length > 0 && (
          <section style={s.section}>
            <h2 style={s.sectionTitle}>Active Pick Unlocks</h2>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {activeUnlocks.map(u => (
                <div key={`${u.basho}/${u.day}`} style={s.unlockRow}>
                  <span style={s.unlockLabel}>
                    Basho <strong>{u.basho}</strong> · Day <strong>{u.day}</strong>
                    <span style={s.unlockBadge}>unlocked</span>
                  </span>
                  <button
                    onClick={() => doRelock(u.basho, u.day)}
                    disabled={relockLoading[`${u.basho}/${u.day}`]}
                    style={s.relockBtn}
                  >
                    {relockLoading[`${u.basho}/${u.day}`] ? '…' : 'Re-lock'}
                  </button>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* ── Robotofuji ── */}
        <section style={s.section}>
          <h2 style={s.sectionTitle}>Robotofuji 🤖</h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>

            {/* Model info card */}
            <div style={{ background: '#FFFFFF', border: '1px solid #D4C8A8', borderRadius: 4, padding: '12px 16px' }}>
              <div style={{ fontSize: 12, fontFamily: font, color: muted, marginBottom: 8, fontWeight: 700, letterSpacing: 0.5 }}>
                ML Model Status
              </div>
              {botModelLoading ? (
                <p style={{ fontSize: 12, color: muted, fontFamily: font, margin: 0 }}>Loading…</p>
              ) : botModel?.model ? (
                <div style={{ fontSize: 12, fontFamily: font, color: ink, display: 'grid', gridTemplateColumns: 'auto auto', gap: '4px 20px' }}>
                  <span style={{ color: muted }}>Version</span><span>v{botModel.model.version}</span>
                  <span style={{ color: muted }}>Accuracy</span><span>{Math.round(botModel.model.accuracy * 100)}%</span>
                  <span style={{ color: muted }}>Trained on</span><span>{botModel.model.trainedOn} bouts</span>
                  <span style={{ color: muted }}>Historical bouts</span><span>{botModel.historicalBouts}</span>
                  <span style={{ color: muted }}>Trained at</span><span>{new Date(botModel.model.createdAt).toLocaleDateString()}</span>
                </div>
              ) : (
                <div style={{ fontSize: 12, fontFamily: font, color: muted }}>
                  No trained model yet.{' '}
                  {botModel?.historicalBouts > 0
                    ? `${botModel.historicalBouts} historical bouts available — click Retrain to train a model.`
                    : 'Run the backfill script first to load historical data.'}
                </div>
              )}
            </div>

            {/* Action buttons */}
            <div style={s.toolRow}>
              <div style={s.toolCard}>
                <div style={s.toolCardTitle}>Retrain Model</div>
                <p style={s.toolCardDesc}>Re-run logistic regression on all HistoricalBout data. Automatically runs after Day 15 each basho.</p>
                <div style={s.toolCardFooter}>
                  <button onClick={retrain} disabled={botRetrainLoading} style={s.toolBtn(false)}>
                    {botRetrainLoading ? 'Training…' : 'Retrain now'}
                  </button>
                  {botRetrainMsg && <span style={s.toolMsg(botRetrainMsg.includes('failed') || botRetrainMsg.includes('error'))}>{botRetrainMsg}</span>}
                </div>
              </div>
              <div style={s.toolCard}>
                <div style={s.toolCardTitle}>Trigger Picks</div>
                <p style={s.toolCardDesc}>Make Robotofuji submit picks for the current basho day using the trained model. Runs automatically each day.</p>
                <div style={s.toolCardFooter}>
                  <button onClick={triggerSmartBotPicks} disabled={smartBotPickLoading || !bashoActive} title={!bashoActive ? 'No active basho' : undefined} style={s.toolBtn(!bashoActive)}>
                    {smartBotPickLoading ? 'Picking…' : 'Pick now'}
                  </button>
                  {smartBotPickMsg && <span style={s.toolMsg(smartBotPickMsg.includes('failed') || smartBotPickMsg.includes('No trained'))}>{smartBotPickMsg}</span>}
                </div>
              </div>
              <div style={s.toolCard}>
                <div style={s.toolCardTitle}>Recent Picks & Reasoning</div>
                <p style={s.toolCardDesc}>View Robotofuji's most recent picks with ML reasoning for each bout.</p>
                <div style={s.toolCardFooter}>
                  <button onClick={() => { setBotPicksOpen(o => !o); if (!botPicksOpen) loadBotPicks() }} style={s.toolBtn(false)}>
                    {botPicksOpen ? 'Hide picks' : 'View recent picks'}
                  </button>
                </div>
              </div>
            </div>

            {/* Recent picks table */}
            {botPicksOpen && (
              <div style={{ background: '#FFFFFF', border: '1px solid #D4C8A8', borderRadius: 4, overflow: 'hidden' }}>
                {botPicksLoading ? (
                  <p style={{ padding: 12, fontSize: 12, color: muted, fontFamily: font, margin: 0 }}>Loading…</p>
                ) : botPicks.length === 0 ? (
                  <p style={{ padding: 12, fontSize: 12, color: muted, fontFamily: font, margin: 0, fontStyle: 'italic' }}>No reasoning data yet.</p>
                ) : (
                  <div style={{ overflowX: 'auto' }}>
                    {botPicks.slice(0, 30).map((p, i) => (
                      <div key={i} style={{ padding: '8px 14px', borderBottom: '1px solid #F5F0E8', fontSize: 11, fontFamily: font }}>
                        <div style={{ display: 'flex', gap: 8, alignItems: 'baseline', flexWrap: 'wrap', marginBottom: 2 }}>
                          <span style={{ fontWeight: 700, color: ink }}>{p.east} vs {p.west}</span>
                          <span style={{ color: muted }}>→ picked</span>
                          <span style={{ color: '#1A6A1A', fontWeight: 700 }}>{p.picked}</span>
                          <span style={{ color: muted }}>({p.reasoning?.confidence}% conf)</span>
                          <span style={{ color: muted, fontSize: 10 }}>Day {p.day}, {p.basho}</span>
                        </div>
                        <div style={{ color: muted, fontSize: 10 }}>
                          {(p.reasoning?.reasons || []).join(' · ')}
                        </div>
                      </div>
                    ))}
                    {botPicks.length > 30 && (
                      <p style={{ padding: '6px 14px', fontSize: 11, color: muted, fontFamily: font, margin: 0 }}>
                        Showing 30 of {botPicks.length} picks
                      </p>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </section>

        {/* ── Groups ── */}
        <section style={s.section}>
          <h2 style={s.sectionTitle}>Groups</h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {groups.map(g => (
              <div key={g.id} style={{ ...s.unlockRow, background: '#FFFFFF', border: '1px solid #D4C8A8', alignItems: 'center' }}>
                {groupEditId === g.id ? (
                  // ── Edit mode ──
                  <>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1 }}>
                      <input
                        autoFocus
                        value={groupEditName}
                        onChange={e => setGroupEditName(e.target.value)}
                        onKeyDown={e => {
                          if (e.key === 'Enter') renameGroup(g.id)
                          if (e.key === 'Escape') { setGroupEditId(null); setGroupEditName('') }
                        }}
                        style={{ ...s.modalInput, flex: 1, margin: 0, padding: '4px 8px', fontSize: 13 }}
                      />
                      <button
                        onClick={() => renameGroup(g.id)}
                        disabled={!groupEditName.trim() || groupRenameLoading}
                        style={s.toolBtn(!groupEditName.trim())}
                      >
                        {groupRenameLoading ? '…' : 'Save'}
                      </button>
                      <button
                        onClick={() => { setGroupEditId(null); setGroupEditName(''); setGroupRenameMsg('') }}
                        style={s.relockBtn}
                      >
                        Cancel
                      </button>
                    </div>
                    {groupRenameMsg && <span style={{ ...s.toolMsg(true), marginLeft: 8 }}>{groupRenameMsg}</span>}
                  </>
                ) : (
                  // ── View mode ──
                  <>
                    <span style={s.unlockLabel}>
                      <strong>{g.name}</strong>
                      <span style={{ color: muted, fontSize: 12 }}> — {g.memberCount} member{g.memberCount !== 1 ? 's' : ''}</span>
                      {g.isDefault && <span style={{ ...s.unlockBadge, background: '#E8F5E8', color: '#1A6A1A', border: '1px solid #A8D4A8' }}>default</span>}
                    </span>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button
                        onClick={() => { setGroupEditId(g.id); setGroupEditName(g.name); setGroupRenameMsg('') }}
                        style={s.relockBtn}
                      >
                        Rename
                      </button>
                      {!g.isDefault && (
                        <button
                          onClick={() => { if (window.confirm(`Delete group "${g.name}"? Members will be moved to the default group.`)) deleteGroup(g.id) }}
                          disabled={groupDeleteLoading[g.id]}
                          style={s.relockBtn}
                        >
                          {groupDeleteLoading[g.id] ? '…' : 'Delete'}
                        </button>
                      )}
                    </div>
                  </>
                )}
              </div>
            ))}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8 }}>
            <input
              value={newGroupName}
              onChange={e => setNewGroupName(e.target.value)}
              placeholder="New group name…"
              onKeyDown={e => { if (e.key === 'Enter') createGroup() }}
              style={{ ...s.modalInput, flex: 1, margin: 0, padding: '6px 10px', fontSize: 12 }}
            />
            <button
              onClick={createGroup}
              disabled={!newGroupName.trim() || groupCreateLoading}
              style={s.toolBtn(!newGroupName.trim())}
            >
              {groupCreateLoading ? '…' : 'Create group'}
            </button>
            {groupCreateMsg && <span style={s.toolMsg(groupCreateMsg.includes('failed') || groupCreateMsg.includes('Error'))}>{groupCreateMsg}</span>}
          </div>
        </section>

        {/* ── Users ── */}
        <section style={s.section}>
          <h2 style={s.sectionTitle}>All Users ({users.length})</h2>
          {loading ? (
            <p style={s.muted}>Loading…</p>
          ) : (
            <div style={s.userList}>
              {all.map(u => (
                <div
                  key={u.username}
                  style={{
                    ...s.userRow,
                    borderColor: u.status === 'pending' ? '#D4C880' : '#D4C8A8',
                    background: u.status === 'pending' ? '#FEFDF0' : '#FFFFFF',
                  }}
                >
                  <div style={s.userLeft}>
                    <div style={s.userMeta}>
                      <span style={s.username}>{u.username}</span>
                      {u.isAdmin && <span style={s.adminTag}>admin</span>}
                      <div style={{ ...s.statusDot, background: statusColor[u.status] }} title={u.status} />
                    </div>
                    <span style={s.userEmail}>{u.email || <em style={{ color: '#B8A88A' }}>no email</em>}</span>
                    <span style={s.joinDate}>{new Date(u.createdAt).toLocaleDateString()}</span>
                  </div>
                  <div style={s.actions}>
                    {groups.length > 0 && (
                      <select
                        value={u.group?.id || ''}
                        onChange={e => assignGroup(u.username, e.target.value)}
                        disabled={groupAssignLoading[u.username]}
                        style={s.groupSelect}
                        title="Group"
                      >
                        {groups.map(g => (
                          <option key={g.id} value={g.id}>{g.name}</option>
                        ))}
                      </select>
                    )}
                    {u.status === 'pending' && (
                      <>
                        <ActionBtn onClick={() => act(u.username, 'approve')} loading={actionLoading[u.username] === 'approve'} color="green">Approve</ActionBtn>
                        <ActionBtn onClick={() => act(u.username, 'reject')} loading={actionLoading[u.username] === 'reject'} color="red">Reject</ActionBtn>
                      </>
                    )}
                    {u.status === 'approved' && u.username !== currentUser && (
                      <ActionBtn onClick={() => setConfirmAction({ type: 'revoke', username: u.username })} loading={actionLoading[u.username] === 'reject'} color="red">Revoke</ActionBtn>
                    )}
                    {u.status === 'rejected' && (
                      <ActionBtn onClick={() => act(u.username, 'approve')} loading={actionLoading[u.username] === 'approve'} color="green">Approve</ActionBtn>
                    )}
                    {u.username !== currentUser && (
                      <ActionBtn
                        onClick={() => setConfirmAction({ type: 'delete', username: u.username })}
                        loading={actionLoading[u.username] === 'delete'}
                        color="red"
                      >
                        Delete
                      </ActionBtn>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>
      </div>

      {/* ── Revoke / Delete confirmation modal ── */}
      {confirmAction && (
        <div style={s.overlay} onClick={() => setConfirmAction(null)}>
          <div style={s.modal} onClick={e => e.stopPropagation()}>
            <div style={s.modalTitle}>
              {confirmAction.type === 'delete' ? `Delete ${confirmAction.username}?` : `Revoke ${confirmAction.username}?`}
            </div>
            <p style={s.modalBody}>
              {confirmAction.type === 'delete'
                ? <>Permanently deletes this account and <strong>all of their picks</strong>. This cannot be undone.</>
                : <>Revokes access — they will no longer be able to log in. Their picks are preserved. You can re-approve them later.</>}
            </p>
            <div style={s.modalBtns}>
              <button onClick={() => setConfirmAction(null)} style={s.modalBtnCancel}>Cancel</button>
              <button
                onClick={() => {
                  if (confirmAction.type === 'delete') deleteUser(confirmAction.username)
                  else act(confirmAction.username, 'reject')
                  setConfirmAction(null)
                }}
                style={s.modalBtnDanger}
              >
                {confirmAction.type === 'delete' ? 'Delete permanently' : 'Revoke access'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Clear data modal ── */}
      {clearStep > 0 && (
        <div style={s.overlay} onClick={closeClear}>
          <div style={s.modal} onClick={e => e.stopPropagation()}>
            {clearStep === 1 && (
              <>
                <div style={s.modalTitle}>Clear all game data?</div>
                <p style={s.modalBody}>
                  This will permanently delete every pick and result record from the database.
                  User accounts will be kept. <strong>This cannot be undone.</strong>
                </p>
                {clearError && <p style={s.modalError}>{clearError}</p>}
                <div style={s.modalBtns}>
                  <button onClick={closeClear} style={s.modalBtnCancel}>Cancel</button>
                  <button onClick={() => { setClearError(''); setClearStep(2) }} style={s.modalBtnDanger}>Continue →</button>
                </div>
              </>
            )}
            {clearStep === 2 && (
              <>
                <div style={s.modalTitle}>Type DELETE to confirm</div>
                <p style={s.modalBody}>Enter the word <strong>DELETE</strong> in all caps to confirm you want to wipe all picks and results.</p>
                <input
                  autoFocus
                  value={clearInput}
                  onChange={e => setClearInput(e.target.value)}
                  placeholder="DELETE"
                  style={s.modalInput}
                  onKeyDown={e => { if (e.key === 'Enter' && clearInput === 'DELETE') doClear() }}
                />
                {clearError && <p style={s.modalError}>{clearError}</p>}
                <div style={s.modalBtns}>
                  <button onClick={closeClear} style={s.modalBtnCancel}>Cancel</button>
                  <button
                    onClick={doClear}
                    disabled={clearInput !== 'DELETE' || clearLoading}
                    style={{ ...s.modalBtnDanger, opacity: clearInput !== 'DELETE' ? 0.4 : 1 }}
                  >
                    {clearLoading ? 'Clearing…' : 'Clear everything'}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* ── Announce modal ── */}
      {announceOpen && (
        <div style={s.overlay} onClick={closeAnnounce}>
          <div style={{ ...s.modal, maxWidth: 520 }} onClick={e => e.stopPropagation()}>
            <div style={s.modalTitle}>Announce to all users</div>
            {!announceConfirm ? (
              <>
                <div style={s.formGroup}>
                  <label style={s.label}>Subject</label>
                  <input
                    value={announceSubject}
                    onChange={e => setAnnounceSubject(e.target.value)}
                    placeholder="e.g. July 2026 basho starts this Saturday!"
                    style={s.modalInput}
                  />
                </div>
                <div style={s.formGroup}>
                  <label style={s.label}>Message</label>
                  <textarea
                    value={announceBody}
                    onChange={e => setAnnounceBody(e.target.value)}
                    placeholder="Write your message here…"
                    rows={6}
                    style={{ ...s.modalInput, resize: 'vertical', lineHeight: 1.5 }}
                  />
                </div>
                {announceMsg && <p style={s.modalError}>{announceMsg}</p>}
                <div style={s.modalBtns}>
                  <button onClick={closeAnnounce} style={s.modalBtnCancel}>Cancel</button>
                  <button
                    onClick={() => setAnnounceConfirm(true)}
                    disabled={!announceSubject.trim() || !announceBody.trim()}
                    style={{ ...s.modalBtnPrimary, opacity: (!announceSubject.trim() || !announceBody.trim()) ? 0.4 : 1 }}
                  >
                    Review & send →
                  </button>
                </div>
              </>
            ) : (
              <>
                <p style={s.modalBody}>
                  Send <strong>"{announceSubject}"</strong> to <strong>{approvedCount} users</strong>?
                </p>
                {announceMsg && <p style={s.modalError}>{announceMsg}</p>}
                <div style={s.modalBtns}>
                  <button onClick={() => setAnnounceConfirm(false)} style={s.modalBtnCancel}>Back</button>
                  <button onClick={sendAnnouncement} disabled={announceLoading} style={s.modalBtnPrimary}>
                    {announceLoading ? 'Sending…' : `Send to ${approvedCount} users`}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* ── Unlock picks modal ── */}
      {unlockOpen && (
        <div style={s.overlay} onClick={closeUnlock}>
          <div style={s.modal} onClick={e => e.stopPropagation()}>
            <div style={s.modalTitle}>Unlock picks</div>
            <p style={s.modalBody}>
              Deleting results for a day will re-enable pick editing for all users. Enter the basho ID and day.
            </p>
            <div style={s.formGroup}>
              <label style={s.label}>Basho (e.g., 202605)</label>
              <input
                autoFocus
                value={unlockBasho}
                onChange={e => setUnlockBasho(e.target.value)}
                placeholder="202605"
                style={s.modalInput}
              />
            </div>
            <div style={s.formGroup}>
              <label style={s.label}>Day (1-15)</label>
              <input
                type="number"
                value={unlockDay}
                onChange={e => setUnlockDay(e.target.value)}
                placeholder="1"
                min="1"
                max="15"
                style={s.modalInput}
              />
            </div>
            {unlockMsg && <p style={s.modalError}>{unlockMsg}</p>}
            <div style={s.modalBtns}>
              <button onClick={closeUnlock} style={s.modalBtnCancel}>Cancel</button>
              <button
                onClick={doUnlock}
                disabled={!unlockBasho || !unlockDay || unlockLoading}
                style={{ ...s.modalBtnPrimary, opacity: (!unlockBasho || !unlockDay) ? 0.4 : 1 }}
              >
                {unlockLoading ? 'Unlocking…' : 'Unlock picks'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

function ActionBtn({ onClick, loading, color, children }) {
  const colors = {
    green: { bg: '#F0FAF0', text: '#1A6A1A', border: '#A8D4A8' },
    red: { bg: '#FEF0F0', text: '#8A1A1A', border: '#D4A8A8' },
    gray: { bg: 'transparent', text: muted, border: '#D4C8A8' },
  }
  const c = colors[color]
  return (
    <button
      onClick={onClick}
      disabled={loading}
      style={{
        padding: '5px 12px', background: c.bg, color: c.text,
        border: `1px solid ${c.border}`, borderRadius: 2,
        cursor: 'pointer', fontFamily: font, fontSize: 11,
        letterSpacing: 1, opacity: loading ? 0.6 : 1,
      }}
    >
      {loading ? '…' : children}
    </button>
  )
}

const s = {
  page: {
    minHeight: '100vh',
    background: '#F5F0E8',
    backgroundImage: 'repeating-linear-gradient(0deg, transparent, transparent 3px, rgba(180,160,120,0.06) 3px, rgba(180,160,120,0.06) 4px)',
    color: ink,
  },
  container: { maxWidth: 860, margin: '0 auto', padding: '28px 20px 60px' },
  header: {
    display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
    marginBottom: 24, flexWrap: 'wrap', gap: 12,
  },
  redLine: { width: 28, height: 3, background: accent, marginBottom: 8 },
  title: { fontFamily: font, fontSize: 28, fontWeight: 900, color: ink, margin: 0 },
  headerRight: { display: 'flex', alignItems: 'center', gap: 12 },
  backLink: { color: muted, textDecoration: 'none', fontFamily: font, fontSize: 12, letterSpacing: 1 },
  pendingBanner: {
    background: '#FEFDF0', border: '1px solid #D4C880',
    borderRadius: 4, padding: '10px 16px', marginBottom: 20,
  },
  pendingBannerText: { color: '#8A6A1A', fontFamily: font, fontSize: 13 },
  section: { marginBottom: 36 },
  sectionTitle: {
    fontFamily: font, fontSize: 12, color: muted, fontWeight: 400,
    marginBottom: 14, borderBottom: '1px solid #D4C8A8', paddingBottom: 8,
    letterSpacing: 2, textTransform: 'uppercase',
  },
  muted: { color: muted, fontFamily: font, fontStyle: 'italic', fontSize: 13 },

  // Tool cards
  toolRow: { display: 'flex', gap: 12, flexWrap: 'wrap' },
  toolCard: {
    flex: '1 1 220px', background: '#FFFFFF', border: '1px solid #D4C8A8',
    borderRadius: 4, padding: '16px', display: 'flex', flexDirection: 'column', gap: 8,
  },
  toolCardTitle: { fontFamily: font, fontSize: 13, fontWeight: 700, color: ink },
  toolCardDesc: { fontFamily: font, fontSize: 12, color: muted, margin: 0, lineHeight: 1.5, flex: 1 },
  toolCardFooter: { display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' },
  toolBtn: (disabled) => ({
    padding: '6px 13px', background: 'transparent', color: disabled ? '#C8BFA8' : muted,
    border: `1px solid ${disabled ? '#E0D8C8' : '#D4C8A8'}`, borderRadius: 2,
    cursor: disabled ? 'default' : 'pointer', fontFamily: font, fontSize: 11, letterSpacing: 1,
  }),
  toolMsg: (isError) => ({
    fontFamily: font, fontSize: 11, color: isError ? accent : '#1A6A1A',
  }),

  // User list
  userList: { display: 'flex', flexDirection: 'column', gap: 6 },
  userRow: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '14px 16px', borderRadius: 4,
    border: '1px solid #D4C8A8', flexWrap: 'wrap', gap: 12,
  },
  userLeft: { display: 'flex', flexDirection: 'column', gap: 3, minWidth: 0 },
  userMeta: { display: 'flex', alignItems: 'center', gap: 8 },
  username: { fontFamily: font, fontSize: 14, color: ink, fontWeight: 700 },
  adminTag: {
    background: '#FEF0F0', color: accent, border: `1px solid ${accent}44`,
    borderRadius: 2, padding: '1px 6px', fontSize: 9, fontFamily: font, letterSpacing: 1,
  },
  statusDot: { width: 7, height: 7, borderRadius: '50%' },
  userEmail: { fontSize: 12, color: muted, fontFamily: font },
  joinDate: { fontSize: 11, color: '#B8A88A', fontFamily: font },
  actions: { display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' },

  // Modal shared
  overlay: {
    position: 'fixed', inset: 0, background: 'rgba(26,10,0,0.45)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    zIndex: 1000, padding: 20,
  },
  modal: {
    background: '#FFFDF8', border: '1px solid #D4C8A8', borderRadius: 6,
    padding: '28px 28px 24px', maxWidth: 420, width: '100%',
    boxShadow: '0 8px 32px rgba(26,10,0,0.18)',
  },
  modalTitle: { fontFamily: font, fontSize: 17, fontWeight: 900, color: ink, marginBottom: 10 },
  modalBody: { fontFamily: font, fontSize: 13, color: muted, lineHeight: 1.6, marginBottom: 16 },
  modalError: { fontFamily: font, fontSize: 12, color: accent, marginBottom: 12 },
  modalInput: {
    width: '100%', boxSizing: 'border-box',
    padding: '8px 10px', fontFamily: font, fontSize: 13,
    border: '1px solid #D4C8A8', borderRadius: 3, background: '#FAFAF8',
    color: ink, outline: 'none',
  },
  modalBtns: { display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 20 },
  modalBtnCancel: {
    padding: '7px 16px', background: 'transparent', color: muted,
    border: '1px solid #D4C8A8', borderRadius: 2,
    cursor: 'pointer', fontFamily: font, fontSize: 12, letterSpacing: 1,
  },
  modalBtnDanger: {
    padding: '7px 16px', background: '#FEF0F0', color: '#8A1A1A',
    border: '1px solid #D4A8A8', borderRadius: 2,
    cursor: 'pointer', fontFamily: font, fontSize: 12, letterSpacing: 1,
  },
  modalBtnPrimary: {
    padding: '7px 16px', background: '#1A0A00', color: '#F5F0E8',
    border: '1px solid #1A0A00', borderRadius: 2,
    cursor: 'pointer', fontFamily: font, fontSize: 12, letterSpacing: 1,
  },
  formGroup: { marginBottom: 14 },
  label: { display: 'block', fontFamily: font, fontSize: 11, color: muted, letterSpacing: 1, marginBottom: 5, textTransform: 'uppercase' },

  // Active unlock list
  unlockRow: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '10px 16px', background: '#FEFDF0', border: '1px solid #D4C880',
    borderRadius: 4,
  },
  unlockLabel: { fontFamily: font, fontSize: 13, color: ink },
  unlockBadge: {
    marginLeft: 10, background: '#FFF8E0', color: '#8A6A1A',
    border: '1px solid #D4C880', borderRadius: 2,
    padding: '1px 7px', fontSize: 10, fontFamily: font, letterSpacing: 1,
  },
  relockBtn: {
    padding: '5px 12px', background: 'transparent', color: muted,
    border: '1px solid #D4C8A8', borderRadius: 2,
    cursor: 'pointer', fontFamily: font, fontSize: 11, letterSpacing: 1,
  },
  groupSelect: {
    padding: '4px 8px', fontFamily: font, fontSize: 11, color: ink,
    border: '1px solid #D4C8A8', borderRadius: 2, background: '#FAFAF8',
    cursor: 'pointer', letterSpacing: 0.5,
  },
}
